import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import AddInIds from "@/constants/AddInIds";
import { decompressData } from "@/utilities/compression";

export const AUDITOR_GROUPS_QUERY_KEY = "logbookAuditAuditorGroups";

/** AddInData `dataType` of the per-database record that limits the auditors' vehicles (kept from v2.5). */
export const AUDITOR_GROUPS_DATA_TYPE = "logbookAuditAuditorGroups";

export interface AuditorGroup {
  /** A vehicle-type group (child of "Typ"). */
  id: string;
  /** False when an administrator switched the type off for the auditors. */
  enabled: boolean;
}

/**
 * The vehicle types an administrator switched off for the customs auditors. Auditors do not
 * see vehicles of a switched-off type; types without an entry and vehicles without a type stay
 * visible.
 */
export interface AuditorGroupSettings {
  id?: string;
  groups: AuditorGroup[];
}

interface RawSettingsDetails {
  dataType?: string;
  groups?: { id?: unknown; enabled?: unknown }[];
}

/** Reads the single per-database record, written uncompressed so Geotab's `whereClause` can find it. */
export function useAuditorGroupSettings() {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery<AuditorGroupSettings>({
    queryKey: [AUDITOR_GROUPS_QUERY_KEY, activeSessionId],
    queryFn: async ({ signal }) => {
      const records = await geotabClient.get(
        "AddInData",
        { addInId: AddInIds.geotabAddin, whereClause: `dataType = "${AUDITOR_GROUPS_DATA_TYPE}"` },
        10,
        signal,
      );
      const record = records[0];
      if (!record) return { groups: [] };
      const details = decompressData<RawSettingsDetails>(record.details);
      const groups: AuditorGroup[] = [];
      for (const entry of details?.groups ?? []) {
        if (typeof entry?.id === "string") groups.push({ id: entry.id, enabled: entry.enabled !== false });
      }
      return { id: record.id, groups };
    },
  });
}

export function useSaveAuditorGroupSettings() {
  const queryClient = useQueryClient();
  const { geotabClient, activeSessionId } = useGeotabClient();
  const queryKey = [AUDITOR_GROUPS_QUERY_KEY, activeSessionId];

  return useMutation({
    mutationFn: async (settings: AuditorGroupSettings): Promise<AuditorGroupSettings> => {
      const entity = {
        ...(settings.id ? { id: settings.id } : {}),
        addInId: AddInIds.geotabAddin,
        details: { dataType: AUDITOR_GROUPS_DATA_TYPE, groups: settings.groups },
      };
      if (settings.id) {
        await geotabClient.call("Set", { typeName: "AddInData", entity });
        return settings;
      }
      // "Add" returns the new entity id; keeping it prevents a second Add before the refetch.
      const id: string = await geotabClient.call("Add", { typeName: "AddInData", entity });
      return { ...settings, id };
    },
    onMutate: async settings => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<AuditorGroupSettings>(queryKey);
      queryClient.setQueryData<AuditorGroupSettings>(queryKey, settings);
      return { previous };
    },
    // The Set response confirms the state, so the cache is authoritative afterwards. A refetch
    // right after the Set can still read the old record from a Geotab replica and would undo
    // the change on screen; only a failed save goes back to the server.
    onSuccess: saved => queryClient.setQueryData<AuditorGroupSettings>(queryKey, saved),
    onError: (_error, _settings, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
